<template>
  <div class="user-mgmt-wrapper">
    <!-- Dialog Components -->

    <!-- Organization Alert Dialog -->
    <div v-if="showOrgAlert" class="dialog-overlay">
      <div class="dialog-box">
        <h3>Missing Information</h3>
        <p>You must select an organization for each user.</p>
        <div class="dialog-actions">
          <button @click="closeOrgAlert">OK</button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div v-if="showDeleteConfirm" class="dialog-overlay">
      <div class="dialog-box">
        <h3>Delete Confirmation</h3>
        <p>Are you sure you want to delete this user?</p>
        <div class="dialog-actions">
          <button @click="deleteUserConfirmed">OK</button>
          <button @click="closeDeleteConfirm">Cancel</button>
        </div>
      </div>
    </div>



    <div class="container">
      <Title>User Management</Title>

      <!-- Add New User Form -->

      <!-- Add New User Form -->
      <div class="card-section">
        <h3>Add New User</h3>
        <div class="user-form">
          <div class="form-grid">


<div>
  <label for="new-username">Username:</label>
  <input type="text" 
         id="new-username" 
         v-model="newUser.username"
         :readonly="!isAdmin"
         :class="{ 
           'input-error': showUsernameError,
           'readonly-input': !isAdmin 
         }" />
  <div v-if="showUsernameError" class="error-message">Please select a different username</div>
</div>


            <div>
              <label for="new-password">Password:</label>
              <input type="password" id="new-password" v-model="newUser.password" />
            </div>

            <div>
              <label for="new-organization">Organization:</label>
              <select id="new-organization" v-model="newUser.organization_id">
                <option value="" disabled selected>Select Organization</option>
                <!-- <option v-for="org in organizations" :key="org.org_id" :value="org.org_id">{{ org.name }}</option> -->
                <option v-for="org in filteredOrganizations" :key="org.org_id" :value="org.org_id">{{ org.name }}</option>
              </select>
            </div>

            <div>
              <label for="new-role">Role:</label>
              <select id="new-role" v-model="newUser.role">
                <option value="" disabled selected>Select Role</option>
                <!-- <option v-for="role in roles" :key="role" :value="role">{{ role }}</option> -->
                <option v-for="role in filteredRoles" :key="role" :value="role">{{ role }}</option>
              </select>
            </div>
          </div>
          <button @click.prevent="addUser">Add User</button>
        </div>
      </div>

      <div class="user-table">
        <table>
          <thead>
            <tr>
              <th @click="sort('id')">ID</th>
              <th @click="sort('username')">Username</th>
              <th @click="sort('organization')">Organization</th>
              <th @click="sort('role')">Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in sortedUsers" :key="user.id">
              <td>{{ user.id }}</td>
              <td>
                <input v-if="isEditing && editingUser.id === user.id" v-model="editingUser.username"
                  class="table-input" />
                <span v-else>{{ user.username }}</span>
              </td>


<td>
  <select v-if="isEditing && editingUser.id === user.id && isAdmin" v-model="editingUser.org_id" class="table-input">
    <option v-for="org in organizations" :key="org.org_id" :value="org.org_id">{{ org.name }}</option>
  </select>
  <span v-else>{{ getOrganizationName(user.org_id) }}</span>
</td>


<td>
  <select v-if="isEditing && editingUser.id === user.id && isAdmin" v-model="editingUser.role" class="table-input">
    <option v-for="role in roles" :key="role" :value="role">{{ role }}</option>
  </select>
  <span v-else>{{ user.role }}</span>
</td>


              <td>
                <button v-if="isEditing && editingUser.id === user.id" class="action-button save-button"
                  @click="updateUser">Save</button>
                <button v-else class="action-button edit-button" @click="editUser(user)">Edit</button>

                <button v-if="isEditing && editingUser.id === user.id" class="action-button cancel-button"
                  @click="cancelEdit">Cancel</button>
                <button v-else class="action-button delete-button" @click="confirmDeleteUser(user)">Delete</button>

                <button v-if="!isEditing || editingUser.id !== user.id" class="action-button reset-password-button"
                  @click="promptResetPassword(user)">Reset</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>






      <!-- Reset Password Confirmation Dialog -->
      <div v-if="showResetConfirm" class="dialog-overlay">
        <div class="dialog-box">
          <h3>Reset Password Confirmation</h3>
          <p>Do you want to reset the password for {{ userToReset?.username }}?</p>
          <div class="dialog-actions">
            <button @click="confirmResetPassword">YES</button>
            <button @click="closeResetConfirm">NO</button>
          </div>
        </div>
      </div>


      <!-- Success Confirmation Dialog -->
      <div v-if="showPasswordResetSuccess" class="dialog-overlay">
        <div class="dialog-box">
          <h3>Password Reset Successful</h3>
          <p>The password for {{ userToReset?.username }} has been changed to: <strong>{{ lastGeneratedPassword
              }}</strong></p>
          <div class="dialog-actions">
            <button @click="closePasswordResetSuccess">OK</button>
          </div>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import Title from './components/Title.vue';

export default {
  name: 'UserMgmtPage',
  components: {
    Title
  },
  props: {
    classes: {
      type: Array,
      default: () => []
    }
  },
  emits: ['updateClasses', 'login', 'logout'],
  data() {
    return {
      users: [],
      organizations: [],
      roles: [],
      isEditing: false,
      editingUser: {},
      newUser: {
        username: '',
        password: '',
        organization_id: '',
        role: ''
      },
      currentSort: 'id',
      currentSortDir: 'asc',
      showOrgAlert: false,     // Control for the org selection dialog
      showDeleteConfirm: false, // Control for delete confirmation dialog
      userToDelete: null,     // Store the user to be deleted
      showUsernameError: false, // Control for username error message
      showResetConfirm: false,
      userToReset: null,
      showPasswordResetSuccess: false, // New state for the final confirmation dialog
      lastGeneratedPassword: '' // Store the last generated password
    };
  },





async mounted() {
  try {
    await Promise.all([this.loadUsers(), this.loadOrganizations()]);
    this.loadRoles();
    
    // Set initial username after users are loaded
    this.newUser.username = this.generateNextUsername();
    
    // Auto-select Org1 for non-admins
    if (!this.isAdmin) {
  const userOrgId = parseInt(localStorage.getItem('org_id')) || 0;
  const userOrg = this.organizations.find(org => org.org_id === userOrgId);
  if (userOrg) {
    this.newUser.organization_id = userOrg.org_id;
  }
  this.newUser.role = 'teacher';
}


  } catch (error) {
    console.error("Error loading initial data:", error);
  }
},


  computed: {

  filteredRoles() {
    if (this.isAdmin) {
      return this.roles; // Admins see all roles
    } else {
      // Non-admins can only create teachers
      return this.roles.filter(role => role === 'teacher');
    }
  },

filteredOrganizations() {
  if (this.isAdmin) {
    return this.organizations;
  } else {
    const userOrgId = parseInt(localStorage.getItem('org_id')) || 0;
    return this.organizations.filter(org => org.org_id === userOrgId);
  }
},


    isAdmin() {
      const userRole = localStorage.getItem('role');
      return userRole === 'admin';
    },



sortedUsers() {
  // Filter users based on role - org_admin only sees org_id=0 users
  let usersToShow = this.users;
  
  const userRole = localStorage.getItem('role');
  if (userRole === 'org_admin') {
    usersToShow = this.users.filter(user => user.org_id === 0);
  }
  // Admin and teacher roles see all users (no filtering)
  
  return usersToShow.sort((a, b) => {
    let modifier = 1;
    if (this.currentSortDir === 'desc') modifier = -1;
    if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;
    if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;
    return 0;
  });
}



  },
  methods: {

generateNextUsername() {
    if (this.users.length === 0) {
      return 'U1';
    }
    
    // Find the highest existing U number
    const existingNumbers = this.users
      .map(user => user.username)
      .filter(username => username.match(/^U\d+$/))
      .map(username => parseInt(username.substring(1)))
      .filter(num => !isNaN(num));
    
    const maxNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
    return `U${maxNumber + 1}`;
  },

    promptResetPassword(user) {
      this.userToReset = user;
      this.showResetConfirm = true;
    },

    closeResetConfirm() {
      this.showResetConfirm = false;
      this.userToReset = null;
    },

async confirmResetPassword() {
  if (this.userToReset) {
    const newPassword = this.generateRandomPassword();
    this.lastGeneratedPassword = newPassword;
    console.log(`New password for ${this.userToReset.username}: ${newPassword}`);

    // Find the organization_id using the organizations array
    const organization = this.organizations.find(org => org.org_id === this.userToReset.org_id);
    const organizationId = organization ? organization.org_id : null;

    if (organizationId === null || organizationId === undefined) {
      console.error('Organization ID not found for the user.');
      this.showNotification('Error: Organization ID not found', 'error');
      return;
    }

    // Updated payload with organization_id
    const updatedUser = {
      username: this.userToReset.username,
      role: this.userToReset.role,
      organization_id: organizationId,
      password: newPassword
    };

    console.log('Payload being sent:', updatedUser);

    try {
      // Update the password
      await apiService.updateUser(this.userToReset.id, updatedUser);
      
      // Set status to require password reset
      await apiService.updateUserStatus(this.userToReset.id, 'password_reset_required');
      
      this.showResetConfirm = false;
      this.showPasswordResetSuccess = true;
    } catch (error) {
      console.error('Error resetting password:', error);
      this.showNotification('Error resetting password', 'error');
    }
  }
},

    closePasswordResetSuccess() {
      this.showPasswordResetSuccess = false;
      // Open the user's default email client with a prefilled subject and body
      const recipient = this.userToReset?.username;
      const subject = encodeURIComponent(`Password Reset for ${this.userToReset?.username}`);
      const body = encodeURIComponent(`Please reset your new password for ${this.userToReset?.username} using this: ${this.lastGeneratedPassword}`);
      const mailtoLink = `mailto:${recipient}?subject=${subject}&body=${body}`;
      this.userToReset = null;

      // Open the mailto link to trigger the email client
      window.location.href = mailtoLink;
    },

    showNotification(message, type = 'info') {
      console.log(`${type}: ${message}`);
    },

    getOrganizationIdForUser(userId) {
      const user = this.users.find(u => u.id === userId);
      return user ? user.organization_id : null;
    },

    generateRandomPassword() {
      const chars = 'abcdefghijkmnpqrstuvwxyz23456789'; // Excludes 'o', '0', '1', 'l'
      let password = '';
      for (let i = 0; i < 8; i++) {
        password += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return password;
    },

    async loadUsers() {
      try {
        this.users = await apiService.fetchUsers();
      } catch (error) {
        console.error("Error loading users:", error);
      }
    },

    async loadOrganizations() {
      try {
        this.organizations = await apiService.fetchOrganizations();
      } catch (error) {
        console.error("Error loading organizations:", error);
      }
    },

    async loadRoles() {
      try {
        const response = await apiService.fetchRoles();
        this.roles = response.roles;
        if (!this.newUser.role && this.roles.length > 0) {
          this.newUser.role = this.roles[0];
        }
      } catch (error) {
        console.error("Error loading roles:", error);
      }
    },

    sort(column) {
      if (this.currentSort === column) {
        this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.currentSort = column;
        this.currentSortDir = 'asc';
      }
    },

    getOrganizationName(orgId) {
      const org = this.organizations.find(org => org.org_id === orgId);
      return org ? org.name : 'Unknown';
    },

    editUser(user) {
      // Завершаем предыдущее редактирование, если оно было
      if (this.isEditing) {
        this.cancelEdit();
      }

      // Начинаем редактирование выбранного пользователя
      this.isEditing = true;
      this.editingUser = { ...user };
    },

    async updateUser() {
      try {
        if (!this.editingUser.org_id) {
          this.showOrgAlert = true;
          return;
        }

        // Создаем правильный объект для обновления
        const userToUpdate = {
          username: this.editingUser.username,
          role: this.editingUser.role,
          organization_id: this.editingUser.org_id
        };

        await apiService.updateUser(this.editingUser.id, userToUpdate);
        await this.loadUsers(); // Перезагружаем список пользователей
        this.cancelEdit();
      } catch (error) {
        console.error("Error updating user:", error);
      }
    },

    cancelEdit() {
      this.isEditing = false;
      this.editingUser = {};
    },

    confirmDeleteUser(user) {
      this.userToDelete = user;
      this.showDeleteConfirm = true;
    },

    async deleteUserConfirmed() {
      try {
        await apiService.deleteUser(this.userToDelete.id);
        await this.loadUsers();
        this.showDeleteConfirm = false;
        this.userToDelete = null;
      } catch (error) {
        console.error("Error deleting user:", error);
      }
    },


async addUser() {
  try {
    // Check if the username already exists
    const existingUser = this.users.find(user => user.username === this.newUser.username);
    if (existingUser) {
      this.newUser.username = this.isAdmin ? '' : this.generateNextUsername();
      this.showUsernameError = true;
      setTimeout(() => {
        this.showUsernameError = false;
      }, 3000);
      return;
    }

    // Check if organization is selected
    // if (!this.newUser.organization_id) {
    if (this.newUser.organization_id === '') {

      this.showOrgAlert = true;
      return;
    }

    await apiService.createUser(this.newUser);
    await this.loadUsers();
    this.newUser = {
      username: this.generateNextUsername(),
      password: '',
      organization_id: '',
      role: this.roles[0]
    };
    this.showUsernameError = false;
  } catch (error) {
    console.error("Error adding user:", error);
  }
},

    async addUserOld() {
      try {
        // Check if the username already exists
        const existingUser = this.users.find(user => user.username === this.newUser.username);
        if (existingUser) {
          this.newUser.username = '';
          this.showUsernameError = true;
          setTimeout(() => {
            this.showUsernameError = false;
          }, 3000);
          return;
        }

        // Check if organization is selected
        // if (!this.newUser.organization_id) {
        if (this.newUser.organization_id === '') {
  
          this.showOrgAlert = true;
          return;
        }

        await apiService.createUser(this.newUser);
        await this.loadUsers();
        this.newUser = {
          username: '',
          password: '',
          organization_id: '',
          role: this.roles[0]
        };
        this.showUsernameError = false;
      } catch (error) {
        console.error("Error adding user:", error);
      }
    },

    closeOrgAlert() {
      this.showOrgAlert = false;
    },

    closeDeleteConfirm() {
      this.showDeleteConfirm = false;
      this.userToDelete = null;
    }
  }
};

</script>


<style scoped>
.user-mgmt-wrapper {
  min-height: 100vh;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  font-family: 'Montserrat', Helvetica, Arial, sans-serif;
  color: #323232;
}

.title {
  color: #323232;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #42b983;
  padding-bottom: 0.5rem;
  display: inline-block;
}

.card-section {
  background-color: white;
  border-radius: 6px;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
}

h1 {
  color: #323232;
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
  font-weight: 600;
  border-bottom: 2px solid #42b983;
  padding-bottom: 0.5rem;
  display: inline-block;
}

h2,
h3 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.user-form {
  background-color: white;
  border-radius: 6px;
  border: 0.5px solid #42b983;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
}

.user-form h2 {
  color: #323232;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 600;
}

.user-form label {
  display: block;
  color: #323232;
  font-size: 14px;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.user-form input,
.user-form select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  height: 36px;
  margin-bottom: 1rem;
  box-sizing: border-box;
}

.user-form input:focus,
.user-form select:focus {
  outline: none;
  border-color: #42b983;
}

.user-form button {
  min-width: 120px;
  height: 36px;
  padding: 0 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
  background-color: #42b983;
  color: white;
  display: block;
  margin-top: 1rem;
}

.user-form button:hover {
  background-color: #2E7D32;
}

.input-error {
  border: 1px solid #f8d7da !important;
}

.error-message {
  color: #dc3545;
  font-size: 12px;
  margin-top: -0.8rem;
  margin-bottom: 1rem;
}

.table-wrapper {
  border-radius: 6px;
  overflow: hidden;
  border-radius: 6px;
  border: 0.5px solid #bbf0db;
}

.user-table {
  margin-bottom: 1.5rem;
  background-color: white;
  border-radius: 6px;
  padding: 1.5rem;
}

.user-table table {
  width: 100%;
  border-collapse: collapse;
  border-spacing: 0;
  font-size: 14px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e8e9f5;
}

.user-table th,
.user-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid #e8e9f5;
  font-weight: 500;
}

.user-table th {
  background-color: #e8f4f0;
  font-weight: 700;
  color: #323232;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.user-table tr:last-child td {
  border-bottom: none;
}

.user-table tr:hover {
  background-color: #f9f9f9;
}

.action-button,
.edit-button,
.delete-button,
.reset-password-button,
.user-form button {
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 5px;
}

.edit-button {
  background-color: #f6f6f6;
  color: #323232;
}

.edit-button:hover {
  background-color: #e8e8e8;
}

.delete-button {
  background-color: #f8d7da;
  color: #dc3545;
}

.delete-button:hover {
  background-color: #f5c6cb;
}

.reset-password-button {
  background-color: #e2f0fd;
  color: #0c5460;
}

.reset-password-button:hover {
  background-color: #d1ecf1;
}

.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
}

.dialog-box {
  background-color: white;
  padding: 1.5rem;
  border-radius: 6px;
  width: 350px;
  border: 1px solid #e8e9f5;
}

.dialog-box h3 {
  margin-top: 0;
  color: #323232;
}

.dialog-box p {
  margin-bottom: 1rem;
  color: #323232;
}

.dialog-actions,
.dialog-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 1rem;
}

.dialog-actions button,
.dialog-buttons button {
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border: none;
}

.dialog-actions button:first-of-type {
  background-color: #505050;
  color: white;
}

.dialog-actions button:last-of-type {
  background-color: #f8d7da;
  color: #721c24;
}

/* Для больших экранов можно сделать форму в сетке */
@media (min-width: 768px) {
  .user-form .form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}

/* Добавить класс table-input */
.table-input {
  width: 100%;
  max-width: 150px;
  padding: 6px 8px;
  border: 1px solid #e8e9f5;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  height: 32px;
}

.table-input:focus {
  outline: none;
  border-color: #505050;
}

/* Стили для кнопок внутри таблицы */
.save-button {
  background-color: #42b983;
  color: white;
}

.save-button:hover {
  background-color: #2E7D32;
}

.cancel-button {
  background-color: #e8e8e8;
  color: #323232;
}

.cancel-button:hover {
  background-color: #d8d8d8;
}

/* Более компактные кнопки для таблицы */
.user-table .action-button {
  min-width: 60px;
  margin-right: 4px;
}

.user-table .reset-password-button {
  min-width: 60px;
}

.readonly-input {
  background-color: #f8f9fa !important;
  color: #6c757d;
  cursor: not-allowed;
}
</style>
